import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Search,
  Plus,
  FolderPlus,
  Eye,
  Send,
  Save,
  Copy,
  Trash2,
  Rocket,
  MapPin,
  CreditCard,
  Calendar,
  Shield,
  BarChart3,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Heading1,
  Heading2,
  List,
  ListOrdered,
  Link2,
  Image as ImageIcon,
  Code as CodeIcon,
  Quote,
  Table as TableIcon,
  Undo2,
  Redo2,
  Folder,
  Clock,
  User as UserIcon,
  ThumbsUp,
} from "lucide-react";
import { toast } from "sonner";

import { AdminShell } from "@/components/admin/admin-shell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/admin/cms")({
  component: Page,
});

type ArticleStatus = "published" | "draft" | "archived";
type Article = {
  id: string;
  categoryId: string;
  title: string;
  status: ArticleStatus;
  updatedAt: string;
  editedAt: string;
  author: string;
  views: number;
  helpful: number;
  featured: boolean;
  body: string;
};
type Category = {
  id: string;
  name: string;
  icon: typeof Rocket;
  tint: string;
};

const INITIAL_CATEGORIES: Category[] = [
  { id: "getting-started", name: "Getting Started", icon: Rocket, tint: "text-[#5958F3]" },
  { id: "managing-spaces", name: "Managing Spaces", icon: MapPin, tint: "text-emerald-600" },
  { id: "billing", name: "Billing & Payments", icon: CreditCard, tint: "text-amber-600" },
  { id: "bookings", name: "Bookings & Scheduling", icon: Calendar, tint: "text-sky-600" },
  { id: "security", name: "Account & Security", icon: Shield, tint: "text-fuchsia-600" },
  { id: "analytics", name: "Analytics & Reports", icon: BarChart3, tint: "text-rose-600" },
];

const DEFAULT_BODY = `> This guide walks you through the process of adding a new space listing to your Spayzo merchant account step by step.

## Before You Begin
Make sure you have the following information ready before starting the listing process:
- Space name, description, and address
- High-quality photos (minimum 3 images, recommended 10+)
- List of available amenities and services
- Availability schedule and pricing details
- Any special policies or house rules

## Step 1 — Basic Information
Navigate to your Merchant Dashboard and click \`Add New Space\`. Fill in the space name, category (e.g. Co-working, Event, Wellness), and a detailed description. Select the mood tags that best describe the atmosphere of your space.

## Step 2 — Media & Amenities
Upload your photos using the drag-and-drop uploader. Ensure your cover image is at least \`1200x800px\`. Check all amenities your space provides — these are shown prominently to users browsing by mood or category.

## Step 3 — Availability
Set your regular weekly hours using the schedule builder. You can configure different hours per day and mark specific dates as unavailable using the date picker. Instant booking can be toggled per timeslot.

## Step 4 — Pricing
Set your hourly or daily rates. You can also add weekend pricing and promotional rates. The platform automatically calculates the guest-facing total including any applicable service fees.`;

const INITIAL_ARTICLES: Article[] = [
  {
    id: "a1",
    categoryId: "managing-spaces",
    title: "How to add a new space listing",
    status: "published",
    updatedAt: "Jun 02, 2025",
    editedAt: "Jun 02, 2025 at 3:44 PM",
    author: "Omar Al Rashid",
    views: 2148,
    helpful: 94,
    featured: true,
    body: DEFAULT_BODY,
  },
  {
    id: "a2",
    categoryId: "managing-spaces",
    title: "Setting space availability & hours",
    status: "published",
    updatedAt: "May 28, 2025",
    editedAt: "May 28, 2025 at 10:12 AM",
    author: "Omar Al Rashid",
    views: 1520,
    helpful: 91,
    featured: false,
    body: "Configure your weekly schedule from the space editor.",
  },
  {
    id: "a3",
    categoryId: "managing-spaces",
    title: "Uploading photos and amenities",
    status: "published",
    updatedAt: "May 20, 2025",
    editedAt: "May 20, 2025 at 5:02 PM",
    author: "Omar Al Rashid",
    views: 987,
    helpful: 88,
    featured: false,
    body: "Best practices for photos and amenities.",
  },
  {
    id: "a4",
    categoryId: "managing-spaces",
    title: "Space pricing and rate cards",
    status: "draft",
    updatedAt: "Jun 01, 2025",
    editedAt: "Jun 01, 2025 at 9:20 AM",
    author: "Omar Al Rashid",
    views: 0,
    helpful: 0,
    featured: false,
    body: "Draft — pricing best practices.",
  },
  {
    id: "a5",
    categoryId: "managing-spaces",
    title: "Managing space reviews",
    status: "published",
    updatedAt: "Apr 14, 2025",
    editedAt: "Apr 14, 2025 at 1:15 PM",
    author: "Omar Al Rashid",
    views: 640,
    helpful: 82,
    featured: false,
    body: "Respond to and moderate reviews.",
  },
  {
    id: "a6",
    categoryId: "managing-spaces",
    title: "Pausing or archiving a space",
    status: "archived",
    updatedAt: "Mar 10, 2025",
    editedAt: "Mar 10, 2025 at 2:00 PM",
    author: "Omar Al Rashid",
    views: 210,
    helpful: 75,
    featured: false,
    body: "Temporarily hide or archive a listing.",
  },
  {
    id: "a7",
    categoryId: "managing-spaces",
    title: "Bulk space import via CSV",
    status: "draft",
    updatedAt: "Jun 03, 2025",
    editedAt: "Jun 03, 2025 at 4:22 PM",
    author: "Omar Al Rashid",
    views: 0,
    helpful: 0,
    featured: false,
    body: "Draft — CSV import.",
  },
  {
    id: "a8",
    categoryId: "getting-started",
    title: "Welcome to Spayzo",
    status: "published",
    updatedAt: "Jan 05, 2025",
    editedAt: "Jan 05, 2025 at 8:00 AM",
    author: "Omar Al Rashid",
    views: 4210,
    helpful: 97,
    featured: true,
    body: "Get started with Spayzo in minutes.",
  },
  {
    id: "a9",
    categoryId: "billing",
    title: "Understanding your payouts",
    status: "published",
    updatedAt: "Feb 12, 2025",
    editedAt: "Feb 12, 2025 at 11:00 AM",
    author: "Omar Al Rashid",
    views: 1830,
    helpful: 90,
    featured: false,
    body: "How and when payouts arrive.",
  },
  {
    id: "a10",
    categoryId: "bookings",
    title: "Instant vs. request bookings",
    status: "published",
    updatedAt: "Mar 22, 2025",
    editedAt: "Mar 22, 2025 at 3:10 PM",
    author: "Omar Al Rashid",
    views: 1105,
    helpful: 89,
    featured: false,
    body: "Choose the right booking mode.",
  },
  {
    id: "a11",
    categoryId: "security",
    title: "Enabling two-factor authentication",
    status: "published",
    updatedAt: "Apr 02, 2025",
    editedAt: "Apr 02, 2025 at 9:45 AM",
    author: "Omar Al Rashid",
    views: 760,
    helpful: 93,
    featured: false,
    body: "Secure your account with 2FA.",
  },
  {
    id: "a12",
    categoryId: "analytics",
    title: "Reading your revenue dashboard",
    status: "published",
    updatedAt: "May 08, 2025",
    editedAt: "May 08, 2025 at 6:30 PM",
    author: "Omar Al Rashid",
    views: 980,
    helpful: 92,
    featured: false,
    body: "Make sense of your revenue metrics.",
  },
];

function Page() {
  const { t } = useTranslation();
  const [articles, setArticles] = useState<Article[]>(INITIAL_ARTICLES);
  const [categories, setCategories] = useState<Category[]>(INITIAL_CATEGORIES);
  const [activeCategoryId, setActiveCategoryId] = useState<string>("managing-spaces");
  const [activeArticleId, setActiveArticleId] = useState<string>("a1");
  const [globalQuery, setGlobalQuery] = useState("");
  const [filterQuery, setFilterQuery] = useState("");

  const activeCategory = categories.find((c) => c.id === activeCategoryId) ?? categories[0];
  const activeArticle = articles.find((a) => a.id === activeArticleId) ?? articles[0];

  const categoryCounts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const a of articles) {
      map[a.categoryId] = (map[a.categoryId] ?? 0) + 1;
    }
    return map;
  }, [articles]);

  const addCategory = (name: string) => {
    const id = `cat-${Date.now()}`;
    const newCat: Category = { id, name, icon: Folder, tint: "text-[#5958F3]" };
    setCategories((prev) => [...prev, newCat]);
    setActiveCategoryId(id);
    toast.success(t("adminCms.toast.categoryCreated", { name }));
  };

  const totals = useMemo(() => {
    const drafts = articles.filter((a) => a.status === "draft").length;
    return { total: articles.length, drafts };
  }, [articles]);

  const categoryArticles = useMemo(() => {
    return articles.filter((a) => {
      if (a.categoryId !== activeCategoryId) return false;
      const q = (globalQuery || filterQuery).trim().toLowerCase();
      if (!q) return true;
      return a.title.toLowerCase().includes(q);
    });
  }, [articles, activeCategoryId, globalQuery, filterQuery]);

  const updateArticle = (patch: Partial<Article>) => {
    setArticles((prev) => prev.map((a) => (a.id === activeArticle.id ? { ...a, ...patch } : a)));
  };

  const addArticle = (categoryId: string) => {
    const id = `a-${Date.now()}`;
    const now = new Date();
    const stamp = now.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
    const newArticle: Article = {
      id,
      categoryId,
      title: t("adminCms.untitled"),
      status: "draft",
      updatedAt: stamp,
      editedAt: `${stamp} at ${now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`,
      author: "Omar Al Rashid",
      views: 0,
      helpful: 0,
      featured: false,
      body: "",
    };
    setArticles((prev) => [newArticle, ...prev]);
    setActiveCategoryId(categoryId);
    setActiveArticleId(id);
    toast.success(t("adminCms.toast.created"));
  };

  const duplicateArticle = () => {
    const id = `a-${Date.now()}`;
    const copy: Article = {
      ...activeArticle,
      id,
      title: `${activeArticle.title} (copy)`,
      status: "draft",
      views: 0,
      helpful: 0,
      featured: false,
    };
    setArticles((prev) => [copy, ...prev]);
    setActiveArticleId(id);
    toast.success(t("adminCms.toast.duplicated"));
  };

  const deleteArticle = () => {
    setArticles((prev) => prev.filter((a) => a.id !== activeArticle.id));
    const remaining = articles.filter((a) => a.categoryId === activeCategoryId && a.id !== activeArticle.id);
    if (remaining[0]) setActiveArticleId(remaining[0].id);
    toast.success(t("adminCms.toast.deleted"));
  };

  const saveDraft = () => {
    updateArticle({ status: "draft" });
    toast.success(t("adminCms.toast.draftSaved"));
  };

  const publish = () => {
    const now = new Date();
    updateArticle({
      status: "published",
      updatedAt: now.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }),
      editedAt: `${now.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })} at ${now.toLocaleTimeString(
        "en-US",
        { hour: "numeric", minute: "2-digit" },
      )}`,
    });
    toast.success(t("adminCms.toast.publishedOk"));
  };

  return (
    <AdminShell
      title={t("pages.admin.cms.title")}
      subtitle={t("pages.admin.cms.subtitle")}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={globalQuery}
              onChange={(e) => setGlobalQuery(e.target.value)}
              placeholder={t("adminCms.searchArticles")}
              className="h-10 w-56 rounded-xl ps-9 md:w-64"
            />
          </div>
          <NewCategoryDialog onAdd={addCategory} />
          <Button
            className="rounded-xl bg-[#5958F3] text-white hover:bg-[#5958F3]/90"
            onClick={() => addArticle(activeCategoryId)}
          >
            <Plus className="h-4 w-4" /> {t("adminCms.newArticle")}
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 gap-0 overflow-hidden rounded-2xl border border-border/60 bg-white shadow-soft lg:grid-cols-[280px_1fr] xl:grid-cols-[280px_1fr_300px]">
        {/* LEFT — Categories & Articles */}
        <aside className="flex flex-col border-b border-border/60 lg:border-b-0 lg:border-e">
          <div className="border-b border-border/60 p-4">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t("adminCms.helpCenter")}
            </h2>
            <div className="mt-3 space-y-1">
              {categories.map((c) => {
                const active = c.id === activeCategoryId;
                return (
                  <button
                    key={c.id}
                    onClick={() => setActiveCategoryId(c.id)}
                    className={cn(
                      "flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-start text-sm font-medium transition",
                      active
                        ? "bg-[#5958F3] text-white"
                        : "text-foreground/70 hover:bg-muted",
                    )}
                  >
                    <c.icon className={cn("h-4 w-4 shrink-0", active ? "text-white" : c.tint)} />
                    <span className="flex-1 truncate">
                      {t(`adminCms.cats.${c.id}`, { defaultValue: c.name })}
                    </span>
                    <span className={cn("text-xs", active ? "text-white/70" : "text-muted-foreground")}>
                      {categoryCounts[c.id] ?? 0}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t("adminCms.articles")}
              </h3>
              <Badge variant="secondary" className="rounded-full text-[10px]">
                {categoryArticles.length}
              </Badge>
            </div>

            <div className="relative mb-3">
              <Search className="pointer-events-none absolute start-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={filterQuery}
                onChange={(e) => setFilterQuery(e.target.value)}
                placeholder={t("adminCms.filterArticles")}
                className="h-8 rounded-lg ps-8 text-xs"
              />
            </div>

            <div className="space-y-2">
              {categoryArticles.map((a) => {
                const active = a.id === activeArticleId;
                return (
                  <button
                    key={a.id}
                    onClick={() => setActiveArticleId(a.id)}
                    className={cn(
                      "block w-full rounded-lg border p-3 text-start transition",
                      active
                        ? "border-[#5958F3] bg-[#5958F3]/5 shadow-sm"
                        : "border-transparent hover:border-border/60 hover:bg-muted/50",
                    )}
                  >
                    <p className="truncate text-sm font-medium">{a.title}</p>
                    <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
                      <StatusDot status={a.status} />
                      <span>{a.updatedAt}</span>
                    </div>
                  </button>
                );
              })}
              {categoryArticles.length === 0 && (
                <p className="py-6 text-center text-xs text-muted-foreground">{t("adminCms.noArticles")}</p>
              )}
            </div>

            <Button
              variant="outline"
              size="sm"
              className="mt-3 w-full rounded-lg border-dashed"
              onClick={() => addArticle(activeCategoryId)}
            >
              <Plus className="h-4 w-4" /> {t("adminCms.newArticle")}
            </Button>
          </div>
        </aside>

        {/* MIDDLE — Editor */}
        <main className="flex min-w-0 flex-col bg-white">
          {/* Header toolbar */}
          <header className="flex min-h-16 flex-wrap items-center justify-between gap-2 border-b border-border/60 px-5 py-3">
            <div className="flex min-w-0 items-center gap-2.5">
              <StatusBadge status={activeArticle.status} />
              <span className="hidden truncate text-sm text-muted-foreground md:inline">
                {t("adminCms.draftSavedAt", { time: activeArticle.editedAt })}
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button variant="ghost" size="sm" className="rounded-lg">
                <Eye className="h-4 w-4" /> <span className="hidden sm:inline">{t("adminCms.preview")}</span>
              </Button>
              <Button variant="outline" size="sm" className="rounded-lg" onClick={saveDraft}>
                <Save className="h-4 w-4" /> <span className="hidden sm:inline">{t("adminCms.saveDraft")}</span>
              </Button>
              <Button
                size="sm"
                className="rounded-lg bg-[#5958F3] text-white hover:bg-[#5958F3]/90"
                onClick={publish}
              >
                <Send className="h-4 w-4" /> {t("adminCms.publishChanges")}
              </Button>
            </div>
          </header>

          {/* Formatting toolbar */}
          <div className="flex flex-wrap items-center gap-0.5 border-b border-border/60 bg-muted/30 px-3 py-1.5">
            <ToolbarBtn icon={Bold} active />
            <ToolbarBtn icon={Italic} />
            <ToolbarBtn icon={Underline} />
            <ToolbarBtn icon={Strikethrough} />
            <ToolbarSep />
            <ToolbarBtn icon={Heading1} />
            <ToolbarBtn icon={Heading2} />
            <ToolbarBtn icon={List} />
            <ToolbarBtn icon={ListOrdered} />
            <ToolbarBtn icon={Quote} />
            <ToolbarSep />
            <ToolbarBtn icon={Link2} />
            <ToolbarBtn icon={ImageIcon} />
            <ToolbarBtn icon={CodeIcon} />
            <ToolbarBtn icon={TableIcon} />
            <div className="ms-auto flex items-center gap-0.5">
              <ToolbarBtn icon={Undo2} />
              <ToolbarBtn icon={Redo2} />
            </div>
          </div>

          {/* Editor content */}
          <div className="flex-1 overflow-y-auto p-6 md:p-10">
            <div className="mx-auto max-w-3xl">
              <Input
                value={activeArticle.title}
                onChange={(e) => updateArticle({ title: e.target.value })}
                className="mb-6 border-0 bg-transparent p-0 font-display text-3xl font-bold shadow-none focus-visible:ring-0"
                placeholder={t("adminCms.untitled")}
              />
              <div className="mb-6 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5 text-[#5958F3]">
                  <Folder className="h-3.5 w-3.5" />
                  {t(`adminCms.cats.${activeCategory.id}`, { defaultValue: activeCategory.name })}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <UserIcon className="h-3.5 w-3.5" /> {activeArticle.author}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <BarChart3 className="h-3.5 w-3.5" /> {t("adminCms.views", { count: activeArticle.views })}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <ThumbsUp className="h-3.5 w-3.5" /> {t("adminCms.helpfulPct", { value: activeArticle.helpful })}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" /> {t("adminCms.lastEdited", { when: activeArticle.editedAt })}
                </span>
              </div>
              <Textarea
                value={activeArticle.body}
                onChange={(e) => updateArticle({ body: e.target.value })}
                placeholder={t("adminCms.bodyPlaceholder")}
                className="min-h-[480px] resize-y rounded-xl border-border/60 bg-background font-mono text-sm leading-relaxed"
              />
            </div>
          </div>
        </main>

        {/* RIGHT — Settings, Stats & Actions */}
        <aside className="space-y-6 border-t border-border/60 p-5 lg:border-t-0 lg:border-s xl:border-s">
          {/* Status & Visibility */}
          <section>
            <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t("adminCms.statusVisibility")}
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="published" className="text-sm">{t("adminCms.published")}</Label>
                <Switch
                  id="published"
                  checked={activeArticle.status === "published"}
                  onCheckedChange={(v) => updateArticle({ status: v ? "published" : "draft" })}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="featured" className="text-sm">{t("adminCms.featuredArticle")}</Label>
                <Switch
                  id="featured"
                  checked={activeArticle.featured}
                  onCheckedChange={(v) => updateArticle({ featured: v })}
                />
              </div>
            </div>
          </section>

          {/* Performance */}
          <section className="border-t border-border/60 pt-5">
            <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t("adminCms.performance")}
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg bg-muted/50 p-3">
                <p className="text-xs text-muted-foreground">{t("adminCms.viewsLabel")}</p>
                <p className="text-xl font-bold">{activeArticle.views.toLocaleString()}</p>
              </div>
              <div className="rounded-lg bg-muted/50 p-3">
                <p className="text-xs text-muted-foreground">{t("adminCms.helpfulLabel")}</p>
                <p className="text-xl font-bold text-emerald-600">{activeArticle.helpful}%</p>
              </div>
            </div>
          </section>

          {/* Actions */}
          <section className="border-t border-border/60 pt-5">
            <div className="space-y-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full rounded-lg"
                onClick={duplicateArticle}
              >
                <Copy className="h-4 w-4" /> {t("adminCms.duplicateArticle")}
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full rounded-lg border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                  >
                    <Trash2 className="h-4 w-4" /> {t("adminCms.deletePermanently")}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t("adminCms.deleteTitle")}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t("adminCms.deleteDesc", { title: activeArticle.title })}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t("adminCms.cancel")}</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-rose-600 hover:bg-rose-700"
                      onClick={deleteArticle}
                    >
                      {t("adminCms.delete")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </section>
        </aside>
      </div>
    </AdminShell>
  );
}

function StatusBadge({ status }: { status: ArticleStatus }) {
  const { t } = useTranslation();
  const map: Record<ArticleStatus, string> = {
    published: "bg-emerald-100 text-emerald-700",
    draft: "bg-amber-100 text-amber-700",
    archived: "bg-muted text-muted-foreground",
  };
  return (
    <span className={cn("inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium", map[status])}>
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-80" />
      {t(`adminCms.status.${status}`)}
    </span>
  );
}

function StatusDot({ status }: { status: ArticleStatus }) {
  const map: Record<ArticleStatus, string> = {
    published: "bg-emerald-500",
    draft: "bg-amber-500",
    archived: "bg-muted-foreground",
  };
  return <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", map[status])} />;
}

function ToolbarBtn({ icon: Icon, active }: { icon: typeof Bold; active?: boolean }) {
  return (
    <button
      type="button"
      className={cn(
        "grid h-8 w-8 place-items-center rounded-md transition hover:bg-background",
        active && "bg-[#5958F3]/10 text-[#5958F3]",
      )}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

function ToolbarSep() {
  return <div className="mx-1 h-5 w-px bg-border/60" />;
}

function NewCategoryDialog({ onAdd }: { onAdd: (name: string) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-xl">
          <FolderPlus className="h-4 w-4" /> <span className="hidden sm:inline">{t("adminCms.newCategory")}</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("adminCms.createCategory")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="cat-name">{t("adminCms.name")}</Label>
          <Input
            id="cat-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("adminCms.namePlaceholder")}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>{t("adminCms.cancel")}</Button>
          <Button
            className="bg-[#5958F3] text-white hover:bg-[#5958F3]/90"
            onClick={() => {
              if (!name.trim()) return;
              onAdd(name.trim());
              setName("");
              setOpen(false);
            }}
          >
            {t("adminCms.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
